import { useState, useEffect, useCallback } from 'react';
import wordpressApi from '../services/wordpressApi';

/**
 * Hook personalizado para manejar datos de WordPress
 * @param {Object} options - Opciones de configuración
 * @returns {Object} Estado y funciones para manejar los datos
 */
export const useWordPressData = (options = {}) => {
    const {
        autoFetch = true,
        area = null,
        searchTerm = null,
        perPage = 100
    } = options;

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [pagination, setPagination] = useState({
        page: 1,
        totalPages: 1,
        total: 0
    });

    /**
     * Función para obtener informes técnicos
     */
    const fetchInformes = useCallback(async (params = {}) => {
        setLoading(true);
        setError(null);

        try {
            let informes;
            
            if (area) {
                informes = await wordpressApi.getInformesByArea(area);
            } else if (searchTerm) {
                informes = await wordpressApi.searchInformes(searchTerm);
            } else {
                informes = await wordpressApi.getInformesTecnicos({
                    per_page: perPage,
                    page: pagination.page,
                    ...params
                });
            }

            setData(informes);
            setPagination(prev => ({
                ...prev,
                total: informes.length
            }));

            return informes;
        } catch (err) {
            setError(err.message);
            console.error('Error fetching informes:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, [area, searchTerm, perPage, pagination.page]);

    /**
     * Función para obtener un informe específico por ID
     */
    const fetchInformeById = useCallback(async (id) => {
        setLoading(true);
        setError(null);

        try {
            const informe = await wordpressApi.getInformeTecnicoById(id);
            return informe;
        } catch (err) {
            setError(err.message);
            console.error('Error fetching informe by ID:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Función para obtener un informe específico por slug
     */
    const fetchInformeBySlug = useCallback(async (slug) => {
        setLoading(true);
        setError(null);

        try {
            const informe = await wordpressApi.getInformeTecnicoBySlug(slug);
            return informe;
        } catch (err) {
            setError(err.message);
            console.error('Error fetching informe by slug:', err);
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Función para buscar informes
     */
    const searchInformes = useCallback(async (term) => {
        setLoading(true);
        setError(null);

        try {
            const results = await wordpressApi.searchInformes(term);
            setData(results);
            return results;
        } catch (err) {
            setError(err.message);
            console.error('Error searching informes:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    /**
     * Función para refrescar los datos
     */
    const refresh = useCallback(() => {
        return fetchInformes();
    }, [fetchInformes]);

    /**
     * Función para limpiar el estado
     */
    const clearData = useCallback(() => {
        setData([]);
        setError(null);
        setPagination({
            page: 1,
            totalPages: 1,
            total: 0
        });
    }, []);

    /**
     * Función para obtener informes por área
     */
    const getInformesByArea = useCallback(async (areaName) => {
        setLoading(true);
        setError(null);

        try {
            const informes = await wordpressApi.getInformesByArea(areaName);
            setData(informes);
            return informes;
        } catch (err) {
            setError(err.message);
            console.error('Error fetching informes by area:', err);
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Efecto para cargar datos automáticamente
    useEffect(() => {
        if (autoFetch) {
            fetchInformes();
        }
    }, [autoFetch, fetchInformes]);

    return {
        // Estado
        data,
        loading,
        error,
        pagination,

        // Funciones
        fetchInformes,
        fetchInformeById,
        fetchInformeBySlug,
        searchInformes,
        getInformesByArea,
        refresh,
        clearData,

        // Utilidades
        isEmpty: data.length === 0,
        hasError: !!error,
        isLoading: loading
    };
};

/**
 * Hook específico para informes de interconexión
 */
export const useInterconnectionReports = () => {
    return useWordPressData({
        area: 'infraestructura', // Buscar por "infraestructura" que es más amplio
        autoFetch: true
    });
};

/**
 * Hook específico para informes de ciberseguridad
 */
export const useCybersecurityReports = () => {
    return useWordPressData({
        area: 'ciberseguridad',
        autoFetch: true
    });
};

/**
 * Hook específico para informes de IPv6
 */
export const useIPv6Reports = () => {
    return useWordPressData({
        area: 'ipv6',
        autoFetch: true
    });
};

/**
 * Hook para cargar un informe específico por slug
 * @param {string} slug - Slug del informe
 * @param {boolean} autoFetch - Si debe cargar automáticamente
 * @param {string} lang - Idioma ('es', 'en', 'pt')
 */
export const useInformeBySlug = (slug, autoFetch = true, lang = 'es') => {
    const [informe, setInforme] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchInforme = useCallback(async () => {
        if (!slug) return;

        setLoading(true);
        setError(null);

        try {
            const data = await wordpressApi.getInformeTecnicoBySlug(slug, lang);
            setInforme(data);
            return data;
        } catch (err) {
            setError(err.message);
            console.error(`Error fetching informe ${slug} (lang: ${lang}):`, err);
            return null;
        } finally {
            setLoading(false);
        }
    }, [slug, lang]);

    useEffect(() => {
        if (autoFetch && slug) {
            fetchInforme();
        }
    }, [autoFetch, slug, lang, fetchInforme]);

    return {
        informe,
        loading,
        error,
        fetchInforme,
        hasData: !!informe,
        hasError: !!error,
        isLoading: loading
    };
};

export default useWordPressData;
